import * as React from 'react';
import { emitHotArea } from '../../shared/eventListener';
import { EventEmitTypes, events } from '../../shared/utils/events';
import { HotAreaEventListenerTypes } from '../../shared/actionHandler/types';
import { generateHandlers } from '../../shared/actionHandler/utils';
import { generateHotareaActionParams } from '../../shared/actionParams';
import { getHotAreaId, withPersistReactEvent } from '../../shared/utils/common';
import { deleteHotAreaNode, setHotAreaNode } from '../../shared/hotAreaNodeMap';

const LONG_PRESS_TIMEOUT = 1000;
const DOUBLE_CLICK_TIMEOUT = 200;
const INTERSECTION_TIMEOUT = 500;

export class HotAreaActionHandler extends React.PureComponent {
  intersectionObserver = null;

  handlerParams = {};

  handlers = {};

  containerRef = null;

  longPressTimer = null;

  preventClick = false;

  doubleClickTimer = null;

  intersectionTimer = null;

  constructor(props) {
    super(props);

    this.handlerParams = { global: props.global, meta: props.meta };
    this.updateHandlersWithParams(props.hotArea.actions);
  }

  componentDidMount() {
    this.initIntersectionObserver();
    this.observeIntersection();

    events.on(EventEmitTypes.HOT_AREA_INTERSECTING_CHANGE, this.onHotAreaIntersectionChange);

    if (this.handlers.onInit) {
      this.handlers.onInit(undefined);
    }

    this.execActionHooks(this.props.hotArea.actions);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.global !== this.props.global || nextProps.meta !== this.props.meta) {
      this.updateHandlerParams(nextProps.meta, nextProps.global);
    }

    if (nextProps.hotArea.actions !== this.props.hotArea.actions) {
      this.updateHandlersWithParams(nextProps.hotArea.actions);
    }
  }

  componentWillUnmount() {
    this.unobserveIntersection();

    events.cancel(EventEmitTypes.HOT_AREA_INTERSECTING_CHANGE, this.onHotAreaIntersectionChange);

    deleteHotAreaNode(this.props.hotArea.kebabCase);

    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer);
    }
  }

  execActionHooks = (actions) => {
    actions.forEach((action) => {
      if (typeof action.action.action === 'function') {
        return;
      }

      const hooks = action.action.action;

      if (hooks.afterHandlerRender) {
        const params = generateHotareaActionParams(
          action,
          this.props.hotArea,
          this.props.instance,
          this.props.global,
          this.props.meta,
        );
        try {
          hooks.afterHandlerRender(params);
        } catch (e) {
          console.error('Action "afterHandlerRender" hook throw error: ', e);
        }
      }
    });
  };

  initIntersectionObserver = () => {
    this.intersectionObserver = new window.IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        const key = parseInt(target.getAttribute('data-key'), 10);
        events.emit(EventEmitTypes.HOT_AREA_INTERSECTING_CHANGE, isIntersecting, key);
      });
    });
  };

  observeIntersection = () => this.intersectionObserver.observe(this.containerRef);

  unobserveIntersection = () => this.intersectionObserver.unobserve(this.containerRef);

  onHotAreaIntersectionChange = (isIntersecting, key) => {
    if (key !== this.props.hotArea.key) {
      return;
    }

    if (this.intersectionTimer) {
      clearTimeout(this.intersectionTimer);
      this.intersectionTimer = null;
    }

    const callback = isIntersecting ? this.onHotAreaEnterView : this.onHotAreaLeaveView;

    this.intersectionTimer = window.setTimeout(() => {
      callback();
      clearTimeout(this.intersectionTimer);
      this.intersectionTimer = null;
    }, INTERSECTION_TIMEOUT);
  };

  onHotAreaEnterView = () => {
    if (this.handlers.onEnterView) {
      this.handlers.onEnterView(undefined);
    }
  };

  onHotAreaLeaveView = () => {
    if (this.handlers.onLeaveView) {
      this.handlers.onLeaveView(undefined);
    }
  };

  onHotAreaPress = withPersistReactEvent((e) => {
    if (!this.handlers.onLongPress) {
      return;
    }

    this.longPressTimer = window.setTimeout(() => this.handlers.onLongPress(e), LONG_PRESS_TIMEOUT);
  });

  onHotAreaPressRelease = () => {
    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer);
    }
    this.longPressTimer = null;
  };

  onSingleClick = withPersistReactEvent((e) => {
    this.doubleClickTimer = window.setTimeout(() => {
      if (!this.preventClick) {
        try {
          this.handlers.onClick(e);
        } catch (e) {
          console.error('Exec onClick action error:', e);
        }
      }
    }, DOUBLE_CLICK_TIMEOUT);
  });

  onDoubleClick = withPersistReactEvent((e) => {
    if (this.doubleClickTimer) {
      clearTimeout(this.doubleClickTimer);
      this.doubleClickTimer = null;
    }

    this.preventClick = true;
    try {
      this.handlers.onDoubleClick(e);
    } catch (e) {
      console.error('Exec onClick action error:', e);
    }

    setTimeout(() => {
      this.preventClick = false;
    }, DOUBLE_CLICK_TIMEOUT);
  });

  emitHotAreaEvent = (e, type) => {
    const {
      props: { hotArea, instance },
      handlerParams: { global, meta },
    } = this;
    return emitHotArea(e, type, hotArea, instance, meta, global);
  };

  withEmitHotAreaEvent = (type, handler) => (e) => {
    if (e) {
      e.persist();
    }

    this.emitHotAreaEvent(e, type);
    if (handler) {
      return handler(e, this.handlerParams);
    }
  };

  updateHandlerParams = (meta, global) => {
    this.handlerParams.meta = meta;
    this.handlerParams.global = global;
  };

  updateHandlersWithParams = (actions) => {
    const {
      onInit,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onDoubleClick,
      onLongPress,
      onEnterView,
      onLeaveView,
    } = generateHandlers(actions, this.props.instance, this.props.hotArea);
    const { withEmitHotAreaEvent, handlers } = this;

    handlers.onInit = withEmitHotAreaEvent(HotAreaEventListenerTypes.INIT, onInit);
    handlers.onClick = withEmitHotAreaEvent(HotAreaEventListenerTypes.CLICK, onClick);
    handlers.onMouseEnter = withEmitHotAreaEvent(HotAreaEventListenerTypes.MOUSE_ENTER, onMouseEnter);
    handlers.onMouseLeave = withEmitHotAreaEvent(HotAreaEventListenerTypes.MOUSE_LEAVE, onMouseLeave);
    handlers.onDoubleClick = withEmitHotAreaEvent(HotAreaEventListenerTypes.DOUBLE_CLICK, onDoubleClick);
    handlers.onLongPress = withEmitHotAreaEvent(HotAreaEventListenerTypes.LONG_PRESS, onLongPress);
    handlers.onEnterView = withEmitHotAreaEvent(HotAreaEventListenerTypes.ENTER_VIEW, onEnterView);
    handlers.onLeaveView = withEmitHotAreaEvent(HotAreaEventListenerTypes.LEAVE_VIEW, onLeaveView);
  };

  setNode = (node) => {
    this.containerRef = node;
    setHotAreaNode(node);
  };

  render() {
    const { children, style = {} } = this.props;
    const { onMouseLeave, onMouseEnter } = this.handlers;

    const elementType = 'div';
    // wx-button 视为 Web Components, 故使用 class, 见 https://reactjs.org/docs/dom-elements.html#classname
    const classKey = 'className';

    return React.createElement(
      elementType,
      {
        [classKey]: '__impage-hot-area-actions-proxy',
        id: getHotAreaId(this.props.hotArea.key),
        ref: this.setNode,
        'data-key': this.props.hotArea.key,
        onClick: this.onSingleClick,
        onDoubleClick: this.onDoubleClick,
        onMouseEnter,
        onMouseLeave,
        onTouchStart: this.onHotAreaPress,
        onTouchEnd: this.onHotAreaPressRelease,
        onMouseDown: this.onHotAreaPress,
        onMouseUp: this.onHotAreaPressRelease,
        style,
      },
      children,
    );
  }
}
